import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { map, remove, get } from 'lodash';
import { browserHistory } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Divider,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SpeakerNotesIcon from '@material-ui/icons/SpeakerNotes';
import IconCompareArrows from '@material-ui/icons/CompareArrows';

import authResource from 'resources/authResource';
import { hasAccessToProduct } from 'utils/permissions';
import IconFileManagement from 'icons/IconFileManagement';
import { Avatar, ChipNew } from 'components/v2';
import { NEXTGEN_ENV_REGEX } from 'containers/App/constants';
import FormattedSimpleMessage from 'core/FormattedSimpleMessage';

import { getInitials, stringToColor } from 'ui-utils';
import storageService from '@lib/services/storageService';
import configService from '@lib/services/configService';
import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';
import DialogSwitchAccount from './DialogSwitchAccount';
import { getMenuItems } from './helper';
import messages from './messages';

const styles = (theme) => {
  const {
    palette: {
      secondary,
      common: { black },
    },
    spacing: { unit },
  } = theme;

  return {
    menu: {
      width: 250,
    },
    menuButtonIcon: {
      color: black,
      fontSize: 16,
      marginLeft: unit + 4,
      position: 'relative',
      top: 1,
    },
    menuButtonText: {
      color: black,
      fontSize: 14,
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
    menuHeader: {
      color: secondary.main,
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 1.7,
    },
    menuItem: {
      padding: `${unit}px ${unit * 3}px`,
    },
    menuItemTextRoot: {
      padding: `0px ${unit}px`,
    },
    menulistItemIconWrapper: {
      marginRight: 0,
    },
    menuItemText: {
      color: black,
      fontSize: 14,
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
    menuList: {
      padding: 0,
      outline: 'none',
    },
    root: {
      display: 'inline-block',
      padding: unit,
      marginLeft: unit,
    },
    menuListChip: {
      marginLeft: unit * 2.5,
    },
  };
};

class HeaderUserMenuButton extends PureComponent {
  constructor(props) {
    super(props);

    this.renderMenuItem = this.renderMenuItem.bind(this);

    this.state = {
      dialogSwitchAccountOpened: false,
      menuAnchor: null,
    };
  }

  handleMenuItemClick = (id) => {
    const { onSignout, user, toggleNextyNext } = this.props;

    switch (id) {
      case 'settings': {
        browserHistory.push('/v2/settings/home');
        break;
      }
      case 'upload':
        browserHistory.push('/uploadcenter');
        break;
      case 'manual':
        window.open('https://app.lumahealth.io/integrations/', '_blank');
        break;
      case 'switch':
        this.setState({ dialogSwitchAccountOpened: true });
        break;
      case 'logout':
        onSignout();
        break;
      case 'ticket':
        {
          const zendeskUser = {
            email: user.email,
            name: `${user.firstname} ${user.lastname}`,
          };
          authResource.zendeskSSO(zendeskUser, 'https://support.lumahealth.io/hc/en-us/requests');
        }
        break;
      case 'file-management':
        browserHistory.push('/file-management');
        break;
      case 'form-responses':
        browserHistory.push('/form-responses');
        break;
      case 'nexty-next':
        toggleNextyNext?.();
        break;
      case 'messaging-activity':
        browserHistory.push('/messaging-activity/summary');
        break;
      case 'admin':
        browserHistory.push('/admin');
        break;
      default:
    }

    this.handleMenuRequestClose();
  };

  handleDialogSwitchAccountRequestClose = () => {
    this.setState({ dialogSwitchAccountOpened: false });
  };

  handleMenuRequestClose = () => {
    this.setState({ menuAnchor: null });
  };

  handleMenuRequestOpen = (event) => {
    const { menuAnchor } = this.state;

    if (!menuAnchor) this.setState({ menuAnchor: event.currentTarget });
  };

  renderUserAvatar(user) {
    const initials = user && getInitials(user.name);
    const color = stringToColor(initials);

    if (user.avatar) {
      const token = storageService.getToken();
      const src = `${configService.getApiBasePath()}/fileUploads/${user.avatar}/file?token=${token}`;
      return <Avatar src={src} />;
    }
    return <Avatar style={{ background: color }}>{initials}</Avatar>;
  }

  getMenuIcon(menuItem) {
    switch (menuItem.id) {
      case 'messaging-activity':
        return <SpeakerNotesIcon color="primary" fontSize="small" />;
      case 'file-management':
        return <IconFileManagement fontSize="small" />;
      case 'form-responses':
        return <IconFileManagement fontSize="small" />;
      case 'nexty-next':
        return <IconCompareArrows fontSize="small" color="primary" />;
      default:
        return (
          <Icon color="primary" fontSize="small">
            {menuItem.icon}
          </Icon>
        );
    }
  }

  renderMenuItem(menu, index) {
    const { classes, permissions, toggleNextyNext } = this.props;
    // give access to file management option on dropdown menu if the user has any of the provisioning set on the array
    const hasAccessToFileManagement = hasAccessToProduct(permissions, [
      ProvisioningEnum.Insurance,
      ProvisioningEnum.PrequalForm,
      ProvisioningEnum.IntakeForm,
      ProvisioningEnum.Feedback,
      // Providers provisioned w/ TH only should see forms - https://app.clickup.com/t/70a6z4
      ProvisioningEnum.Telehealth,
    ]);
    const isNextGenEnv = NEXTGEN_ENV_REGEX.test(window.origin);

    if (menu.id === 'file-management' && !hasAccessToFileManagement) return null;
    if (menu.id === 'form-responses' && !hasAccessToProduct(permissions, RolloutEnum.BiDiForms) && !isNextGenEnv) {
      return null;
    }
    if (menu.id === 'admin' && !permissions.hasAccessAdminPage) return null;
    if (menu.id === 'upload' && !hasAccessToProduct(permissions, ProvisioningEnum.Upload)) return null;
    if (menu.id === 'nexty-next' && !toggleNextyNext) return null;
    if (menu.id === 'divider') return <Divider key={`divider_${index}`} />;

    return (
      <ListItem
        button
        className={classes.menuItem}
        key={menu.id}
        data-lh-id={`menu-${menu.id}-item`}
        id={`menu-${menu.id}-item`}
        onClick={() => this.handleMenuItemClick(menu.id)}
      >
        <ListItemIcon className={classes.menulistItemIconWrapper}>{this.getMenuIcon(menu)}</ListItemIcon>
        <ListItemText
          classes={{ primary: classes.menuItemText, root: classes.menuItemTextRoot }}
          primary={
            <div>
              {typeof menu.label === 'object' ? <FormattedSimpleMessage {...menu.label} /> : menu.label}
              {menu.isNew && <ChipNew className={classes.menuListChip} />}
            </div>
          }
        />
      </ListItem>
    );
  }

  renderMenu() {
    const { accounts, classes, user, enableViewTicket } = this.props;

    const { menuAnchor } = this.state;
    const initialMenus = [
      {
        id: 'settings',
        icon: 'settings',
        label: messages.missionControl,
      },
      {
        id: 'upload',
        icon: 'file_upload',
        label: messages.uploadCenter,
      },
      {
        id: 'file-management',
        icon: 'file_management',
        label: messages.fileManagement,
      },
      {
        id: 'nexty-next',
        label: messages.nextyNext,
      },
      {
        id: 'form-responses',
        icon: 'file_management',
        label: messages.formResponses,
      },
      {
        id: 'messaging-activity',
        label: messages.messagingActivity,
      },
      {
        id: 'switch',
        icon: 'person',
        label: messages.switchAccounts,
      },
      {
        icon: 'build_black',
        label: 'Multi-Account Access',
        id: 'admin',
      },
      {
        id: 'divider',
      },
      {
        id: 'logout',
        icon: 'input',
        label: messages.logout,
      },
      {
        id: 'divider',
      },
      {
        id: 'ticket',
        icon: 'receipt',
        label: messages.openTicket,
      },
    ];

    const menus = getMenuItems(initialMenus, enableViewTicket);

    if (accounts.length === 0) remove(menus, (menu) => menu.id === 'switch');

    const listHeader = (
      <ListSubheader className={classes.menuHeader} component="div">
        {user.name}
      </ListSubheader>
    );

    return (
      <Menu
        anchorEl={menuAnchor}
        MenuListProps={{ classes: { root: classes.menu } }}
        onClose={this.handleMenuRequestClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={!!menuAnchor}
      >
        <List className={classes.menuList} component="nav" subheader={listHeader}>
          {map(menus, this.renderMenuItem)}
        </List>
      </Menu>
    );
  }

  render() {
    const { accounts, classes, onSwitchAccount, user, root, avatar } = this.props;
    const { dialogSwitchAccountOpened } = this.state;

    return (
      <div className={classes.root}>
        <Button
          classes={{
            label: classes.menuButtonText,
          }}
          disableFocusRipple
          onClick={this.handleMenuRequestOpen}
          size={!avatar ? 'large' : 'medium'}
          variant="text"
          data-lh-id="header-user-menu-button"
        >
          {avatar ? (
            this.renderUserAvatar(user)
          ) : (
            <React.Fragment>
              {get(user, 'name')}
              {get(user, 'id') === get(root, 'id') ? '' : ` - ${get(root, 'name')}`}
              <KeyboardArrowDownIcon className={classes.menuButtonIcon} />
            </React.Fragment>
          )}
        </Button>
        {this.renderMenu()}
        <DialogSwitchAccount
          accounts={accounts}
          onRequestClose={this.handleDialogSwitchAccountRequestClose}
          onSelectAccount={onSwitchAccount}
          open={dialogSwitchAccountOpened}
        />
      </div>
    );
  }
}

HeaderUserMenuButton.propTypes = {
  accounts: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  onSignout: PropTypes.func.isRequired,
  onSwitchAccount: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  root: PropTypes.object,
  permissions: PropTypes.object.isRequired,
  toggleNextyNext: PropTypes.func,
  avatar: PropTypes.bool,
  enableViewTicket: PropTypes.bool,
};

export default withStyles(styles)(HeaderUserMenuButton);
